import { useState } from 'react';
import {
  User,
  Bot,
  Quote,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { ChatMessage as ChatMessageType, CitedPassage } from '@/features/chat/hooks/useChat';
import { format } from 'date-fns';

// =========================================
// InlineCitation — cited passage within a message
// =========================================

interface InlineCitationProps {
  passage: CitedPassage;
  index: number;
  isHighlighted?: boolean;
  onCitationClick?: (passage: CitedPassage) => void;
}

function InlineCitation({ passage, index, isHighlighted, onCitationClick }: InlineCitationProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(passage.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={cn(
        'group border-l-2 rounded-r-md p-2 my-1.5 cursor-pointer transition-colors',
        isHighlighted
          ? 'border-yellow-400 bg-yellow-50'
          : 'border-indigo-300 bg-indigo-50/50 hover:bg-indigo-50'
      )}
      onClick={() => onCitationClick?.(passage)}
    >
      <div className="flex items-start gap-1.5">
        <Quote className="h-3 w-3 text-indigo-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-600 italic leading-relaxed">
            &ldquo;{passage.text}&rdquo;
          </p>
          {passage.relevance && (
            <p className="text-[10px] text-slate-400 mt-1">
              {passage.relevance}
            </p>
          )}
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                handleCopy();
              }}
            >
              {copied ? (
                <Check className="h-2.5 w-2.5 text-emerald-500" />
              ) : (
                <Copy className="h-2.5 w-2.5 text-slate-400" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">{copied ? 'Copied!' : 'Copy'}</p>
          </TooltipContent>
        </Tooltip>
      </div>
      <Badge variant="outline" className="text-[9px] mt-1 h-4">
        Citation {index + 1}
        {isHighlighted && (
          <span className="ml-1 text-yellow-600">highlighted</span>
        )}
      </Badge>
    </div>
  );
}

// =========================================
// ChatMessage — single message bubble
// =========================================

interface ChatMessageProps {
  message: ChatMessageType;
  className?: string;
  highlightedPassage?: string;
  onCitationClick?: (passage: CitedPassage) => void;
}

export function ChatMessage({ message, className, highlightedPassage, onCitationClick }: ChatMessageProps) {
  const [showCitations, setShowCitations] = useState(true);
  const isUser = message.role === 'user';
  const hasCitations =
    message.cited_passages && message.cited_passages.length > 0;

  return (
    <div
      className={cn(
        'flex gap-3',
        isUser ? 'flex-row-reverse' : 'flex-row',
        className
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex items-center justify-center h-8 w-8 rounded-full shrink-0',
          isUser ? 'bg-indigo-100' : 'bg-slate-100'
        )}
      >
        {isUser ? (
          <User className="h-4 w-4 text-indigo-600" />
        ) : (
          <Bot className="h-4 w-4 text-slate-600" />
        )}
      </div>

      {/* Content */}
      <div
        className={cn(
          'flex-1 max-w-[80%] space-y-1',
          isUser ? 'items-end' : 'items-start'
        )}
      >
        <div
          className={cn(
            'rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
            isUser
              ? 'bg-indigo-600 text-white rounded-tr-sm'
              : 'bg-slate-100 text-slate-800 rounded-tl-sm'
          )}
        >
          {/* Render content with simple line breaks */}
          {message.content.split('\n').map((line, i, arr) => (
            <span key={i}>
              {line}
              {i < arr.length - 1 && <br />}
            </span>
          ))}
        </div>

        {/* Citations toggle + list */}
        {hasCitations && (
          <div className="mt-1.5">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-[11px] text-indigo-500 hover:text-indigo-700 px-2"
              onClick={() => setShowCitations((prev) => !prev)}
            >
              <Quote className="h-3 w-3 mr-1" />
              {message.cited_passages.length} citation
              {message.cited_passages.length !== 1 ? 's' : ''}
              {showCitations ? (
                <ChevronUp className="h-3 w-3 ml-1" />
              ) : (
                <ChevronDown className="h-3 w-3 ml-1" />
              )}
            </Button>
            {showCitations && (
              <div className="mt-1 space-y-1">
                {message.cited_passages.map((passage, i) => (
                  <InlineCitation
                    key={i}
                    passage={passage}
                    index={i}
                    isHighlighted={highlightedPassage === passage.text}
                    onCitationClick={onCitationClick}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Timestamp */}
        <p
          className={cn(
            'text-[10px] text-slate-400 px-1',
            isUser ? 'text-right' : 'text-left'
          )}
        >
          {message.created_at
            ? format(new Date(message.created_at), 'HH:mm')
            : ''}
        </p>
      </div>
    </div>
  );
}
